import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface UnmDomain {
  domain_id: number;
  domain_name: string;
  tiers?: { tier_id: number; tier_name: string; hierarchy_level: number }[];
}

export interface UnmAsset {
  asset_id: number;
  domain_id: number;
  domain_name?: string;
  tier_id?: number;
  tier_name?: string;
  asset_type: string;
  asset_code: string;
  asset_name: string;
  route_id?: number;
  chainage_m?: number;
  offset_distance?: number;
  offset_side?: string;
  depth_m?: number;
  status?: string;
  node_id?: number;
  properties?: Record<string, unknown>;
  lrs_utility_asset_id?: number;
  geom_geojson?: GeoJSON.Point;
}

export interface UnmTraceRow {
  node_id: number;
  asset_id: number;
  asset_name: string;
  asset_type?: string;
  depth: number;
  is_isolator?: boolean;
  geom_geojson?: GeoJSON.Point | GeoJSON.LineString;
}

export interface UnmTraceEdge {
  edge_id: number;
  from_node_id: number;
  to_node_id: number;
  geom_geojson?: GeoJSON.LineString;
}

export interface UnmPermitAssessResult {
  riskScore: number;
  riskLevel: string;
  requiredApprovals: string[];
  impactedDirectAssets: unknown[];
  dependencyAssets: unknown[];
  affectedOutageAssets: unknown[];
  status?: string;
  lrsClash?: unknown;
  permit?: unknown;
  outage?: { outage_id?: number; affected_count?: number; cause?: string; error?: string };
}

export interface UnmNetworkRule {
  rule_id: number;
  from_asset_type: string;
  to_asset_type: string;
  connection_type: string;
  allowed: boolean;
}

export interface UnmPropertyField {
  name: string;
  type: string;
  label: string;
  min?: number;
  values?: string[];
}

export const UNM_ASSET_PROPERTY_FIELDS: Record<string, UnmPropertyField[]> = {
  FIBER: [{ name: 'fiber_count', type: 'integer', label: 'Fiber count', min: 1 }],
  CABINET: [{ name: 'port_count', type: 'integer', label: 'Port count', min: 1 }],
  CABLE: [{ name: 'voltage', type: 'number', label: 'Voltage (kV)', min: 0 }],
  TRANSFORMER: [
    { name: 'kv_rating', type: 'number', label: 'kV rating', min: 0 },
    { name: 'mva_rating', type: 'number', label: 'MVA rating', min: 0 },
  ],
  PIPE: [
    { name: 'diameter', type: 'number', label: 'Diameter (mm)', min: 0 },
    { name: 'material', type: 'string', label: 'Material' },
    { name: 'pressure_class', type: 'string', label: 'Pressure class' },
  ],
  VALVE: [
    {
      name: 'valve_type',
      type: 'enum',
      label: 'Valve type',
      values: ['Sluice', 'Gate', 'Ball', 'Butterfly', 'Air Release', 'Pressure Reducing'],
    },
  ],
  DUCT: [{ name: 'diameter', type: 'number', label: 'Diameter (mm)', min: 0 }],
  SUBSTATION: [{ name: 'voltage', type: 'number', label: 'Voltage (kV)', min: 0 }],
};

/** Client-side preflight allow-list (server is authoritative). */
export const UNM_CLIENT_CONNECT_RULES = [
  'SUBSTATION-CABLE',
  'CABLE-TRANSFORMER',
  'TRANSFORMER-STREET_LIGHT',
  'FIBER-CABINET',
  'CABINET-FIBER',
  'JUNCTION-FIBER',
  'FIBER-JUNCTION',
  'JUNCTION-CABLE',
  'CABLE-JUNCTION',
  'VALVE-PIPE',
  'PIPE-VALVE',
];

export type UnmTraceDirection = 'downstream' | 'upstream' | 'connected' | 'isolation';

@Injectable({ providedIn: 'root' })
export class UnmService {
  private readonly apiUrl = `${environment.BASE_URL}unm`;

  constructor(private http: HttpClient) {}

  health(): Observable<{ success: boolean; data?: unknown }> {
    return this.http.get<{ success: boolean; data?: unknown }>(`${this.apiUrl}/health`);
  }

  listDomains(): Observable<{ success: boolean; data: UnmDomain[] }> {
    return this.http.get<{ success: boolean; data: UnmDomain[] }>(`${this.apiUrl}/domains`);
  }

  listAssets(params: {
    route_id?: number;
    domain_id?: number;
    asset_type?: string;
    limit?: number;
  } = {}): Observable<{ success: boolean; data: UnmAsset[] }> {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') httpParams = httpParams.set(k, String(v));
    });
    return this.http.get<{ success: boolean; data: UnmAsset[] }>(`${this.apiUrl}/assets`, {
      params: httpParams,
    });
  }

  listEdges(params: { route_id?: number } = {}): Observable<{
    success: boolean;
    data: UnmTraceEdge[];
  }> {
    let httpParams = new HttpParams();
    if (params.route_id != null) httpParams = httpParams.set('route_id', String(params.route_id));
    return this.http.get<{ success: boolean; data: UnmTraceEdge[] }>(`${this.apiUrl}/edges`, {
      params: httpParams,
    });
  }

  createAsset(body: Record<string, unknown>): Observable<{ success: boolean; data: UnmAsset }> {
    return this.http.post<{ success: boolean; data: UnmAsset }>(`${this.apiUrl}/assets`, body);
  }

  updateAsset(
    assetId: number,
    body: Record<string, unknown>
  ): Observable<{ success: boolean; data: UnmAsset }> {
    return this.http.put<{ success: boolean; data: UnmAsset }>(
      `${this.apiUrl}/assets/${assetId}`,
      body
    );
  }

  materializeGeom(assetId: number): Observable<{ success: boolean; data: UnmAsset }> {
    return this.http.post<{ success: boolean; data: UnmAsset }>(
      `${this.apiUrl}/assets/${assetId}/materialize-geom`,
      {}
    );
  }

  connect(body: {
    fromNodeId: number;
    toNodeId: number;
    flowDirection?: string;
  }): Observable<{ success: boolean; data: unknown; message?: string }> {
    return this.http.post<{ success: boolean; data: unknown; message?: string }>(
      `${this.apiUrl}/connect`,
      body
    );
  }

  trace(
    nodeId: number,
    direction: UnmTraceDirection = 'downstream'
  ): Observable<{ success: boolean; trace_results: UnmTraceRow[]; trace_edges?: UnmTraceEdge[] }> {
    return this.http.get<{
      success: boolean;
      trace_results: UnmTraceRow[];
      trace_edges?: UnmTraceEdge[];
    }>(`${this.apiUrl}/trace/${nodeId}`, {
      params: new HttpParams().set('direction', direction),
    });
  }

  addContainment(
    containerAssetId: number,
    containedAssetId: number
  ): Observable<{ success: boolean; data: unknown }> {
    return this.http.post<{ success: boolean; data: unknown }>(
      `${this.apiUrl}/associations/containment`,
      { containerAssetId, containedAssetId }
    );
  }

  addAttachment(
    structureAssetId: number,
    domainAssetId: number
  ): Observable<{ success: boolean; data: unknown }> {
    return this.http.post<{ success: boolean; data: unknown }>(
      `${this.apiUrl}/associations/attachment`,
      { structureAssetId, domainAssetId }
    );
  }

  listRules(): Observable<{ success: boolean; data: UnmNetworkRule[] }> {
    return this.http.get<{ success: boolean; data: UnmNetworkRule[] }>(
      `${this.apiUrl}/rules/network`
    );
  }

  assessPermit(body: Record<string, unknown>): Observable<{
    success: boolean;
    data: UnmPermitAssessResult;
  }> {
    return this.http.post<{ success: boolean; data: UnmPermitAssessResult }>(
      `${this.apiUrl}/permit/assess`,
      body
    );
  }

  validateConnectionRulesClient(sourceType: string, targetType: string): boolean {
    const key = `${sourceType.toUpperCase()}-${targetType.toUpperCase()}`;
    return UNM_CLIENT_CONNECT_RULES.includes(key);
  }

  getTierStyleColor(tier?: string): string {
    switch ((tier || '').toUpperCase()) {
      case 'TRANSMISSION':
        return '#ff1744';
      case 'DISTRIBUTION':
        return '#2979ff';
      default:
        return '#00e676';
    }
  }

  propertyFieldsFor(assetType: string): UnmPropertyField[] {
    return UNM_ASSET_PROPERTY_FIELDS[String(assetType || '').toUpperCase()] || [];
  }
}
